import { ChangeDetectionStrategy, ChangeDetectorRef, Component, OnInit } from '@angular/core';
import { MatDialogRef } from '@angular/material/dialog';
import { Router } from '@angular/router';
import { INVALID_MATCHING_PERIOD_NAME, MatchingPeriodService } from '../../services/matchingPeriod.service';
import { MatDialogContent } from '@angular/material/dialog';
import { MatDialogActions } from '@angular/material/dialog';
import { MatButtonModule } from '@angular/material/button';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { FormsModule } from '@angular/forms';
import { CommonModule } from '@angular/common';
import { NotifyService } from '../../common/notify/notify.service';

// Matching period names are "YYYY-N" with an optional sub-period suffix, e.g. "2026-4.1"
const MATCHING_PERIOD_NAME_PATTERN = /^\d{4}-[1-9]\d*(\.\d+)?$/;

@Component({
    imports: [CommonModule, FormsModule, MatDialogContent, MatDialogActions, MatButtonModule, MatFormFieldModule, MatInputModule],
    selector: 'app-matchingPeriod-dialog',
    templateUrl: './matchingPeriod-dialog.component.html',
    styleUrl: './matchingPeriod-dialog.component.scss',
    providers: [NotifyService],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class MatchingPeriodDialogComponent implements OnInit {
  isCreating = false; // disable create button while creating to prevent multiple clicks
  isLoadingName = true;
  isEditingName = false;
  matchingPeriodName = '';
  nameError: string | null = null;

  constructor(
    private readonly matchingPeriodService: MatchingPeriodService,
    private readonly router: Router,
    private readonly dialogRef: MatDialogRef<MatchingPeriodDialogComponent>,
    private readonly notify: NotifyService,
    private readonly changeDetector: ChangeDetectorRef
  ) {}

  ngOnInit(): void {
    // Propose the next whole quarter (2026-3 and 2026-3.1 both propose 2026-4, after 2026-4 comes 2027-1)
    this.matchingPeriodService.getNextMatchingPeriodName().subscribe({
      next: (matchingPeriodName) => {
        this.matchingPeriodName = matchingPeriodName;
        this.isLoadingName = false;
        this.changeDetector.markForCheck();
      },
      error: () => {
        // Could not determine a suggestion, let the user type the name themselves
        this.isLoadingName = false;
        this.isEditingName = true;
        this.nameError = 'Could not determine the next matching period name, please enter it manually.';
        this.changeDetector.markForCheck();
      }
    });
  }

  startEditingName(): void {
    this.isEditingName = true;
  }

  onNameChange(): void {
    this.nameError = null;
  }

  async createNew(): Promise<void> {
    if (this.isCreating || this.isLoadingName) return;

    const matchingPeriodName = this.matchingPeriodName.trim();
    if (!MATCHING_PERIOD_NAME_PATTERN.test(matchingPeriodName)) {
      this.nameError = 'Use the format YYYY-N or YYYY-N.M, for example 2027-1 or 2026-4.1.';
      this.changeDetector.markForCheck();
      return;
    }

    this.isCreating = true;
    this.nameError = null;

    this.matchingPeriodService.createMatchingPeriod(matchingPeriodName).subscribe({
      next: (response) => {
        this.notify.withAutodismiss(`New matching period: ${response.matchingPeriodName} started`);
        this.dialogRef.close(response.matchingPeriodName); // Close dialog and pass the matchingPeriod name back
      },
      error: (err) => {
        console.log(err);
        if (err?.name === INVALID_MATCHING_PERIOD_NAME) {
          // Keep the dialog open on a rejected name so the user can correct it
          this.isCreating = false;
          this.isEditingName = true;
          this.nameError = err.message;
          this.changeDetector.markForCheck();
          return;
        }
        this.notify.withAutodismiss('Failed to start new matching period, please contact a moderator.');
        this.dialogRef.close('N/A'); // Close dialog with error handling
      }
    });

    //await this.router.navigateByUrl('/participants');
  }

  async close() {
    this.dialogRef.close();
  }
}
